
import * as turf from '@turf/turf';
import { kml } from '@tmcw/togeojson';
import { area, polygon } from '@turf/turf';
import { DOMParser } from 'xmldom';
import API from "../BaseURL";

const configJson = {
    headers: {
      "content-type": "application/json",
    },
  };

  const configFormData = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };
  

export async function getAllExploitations() {
    const res = API.get(`/exploitations/`, configJson);
    return (await res).data;
}

export async function getExploitationByClientId(client_id) {
    const res = API.get(`/exploitations/user/${client_id}`, configJson);
    return (await res).data;
}


export async function getExploitationsByManager(manager_id) {
    if (!manager_id) {
      return;
    }
    const res = API.get(`/exploitations/manager/${manager_id}`, configJson);
    return (await res).data;
}



export async function getExploitationsByDomainManager(domain_manager_id) {
    if (!domain_manager_id) {
      return;
    }
    const res = API.get(`/exploitations/domain_manager/${domain_manager_id}`, configJson);
    return (await res).data;
}

export async function getDomains(user_id) {
  const alldomains = await API.get(`/domains/`, configJson);
  const res = alldomains.data.filter(domain => domain.user_id === user_id);
  return res;
}

export async function getExploitationInfo(exploitation_id) {
    const res = API.get(`/exploitations/${exploitation_id}`, configJson);
    return res;
}

export async function createExploitation(data) {
    const res = API.post(`/exploitations/`, data, configFormData);
    return res;
}


export async function updateExploitation(exploitation_id, data) {
  const res = API.put(`/exploitations/${exploitation_id}/`, data, configJson);
  return res;
}


export async function updateSupportedAnalysis(exploitation_id, data) {
  const res = API.put(`/exploitations/${exploitation_id}/analysis/`, data, {
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
  });
  return res;
}


export async function deleteExploitation(exploitation_id) {
    const res = API.delete(`/exploitations/${exploitation_id}/`, configJson);
    return res;
}

export async function getExploitationKmlFiles(exploitation_id) {
  const res = API.get(`/files/exploitation/${exploitation_id}/`, configJson);
  return res;
}



export async function uploadExploitationKmlFiles(exploitation_id, files) {
  const res = API.put(`/files/exploitation/${exploitation_id}`, files, configFormData);
  return res;
}



export async function deleteExploitationFile(exploitation_id, file_path) {
  const res = API.delete(`/files/exploitation/${exploitation_id}/${file_path}`, configJson);
  return res;
}


export async function calculateTotalSurface(kmlFiles) {
  let totalArea = 0;

  try {
    for (const kmlFile of kmlFiles) {
      // Read the KML file
      const kmlText = await kmlFile.text();

      // Parse the KML
      const parser = new DOMParser();
      const kmlDom = parser.parseFromString(kmlText, 'text/xml');

      // Convert KML to GeoJSON
      const geoJson = kml(kmlDom);

      // Iterate through features in the GeoJSON
      geoJson.features.forEach((feature) => {
        if (feature.geometry.type === 'Polygon') {
          // Calculate area for each polygon
          const polygonArea = area(polygon(feature.geometry.coordinates));
          totalArea += polygonArea;
        } else if (feature.geometry.type === 'MultiPolygon') {
          // Handle MultiPolygon geometries
          feature.geometry.coordinates.forEach((polygonCoords) => {
            const polygonArea = area(polygon(polygonCoords));
            totalArea += polygonArea;
          });
        }
      });
    }

    // Convert square meters to hectares
    const areaInHectares = totalArea / 10000;

    return areaInHectares.toFixed(2); // Return area rounded to 2 decimal places
  } catch (error) {
    console.error('Error calculating surface:', error);
    throw new Error('Failed to calculate surface area from KML files');
  }
}




const parseKMLFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(e.target.result, 'text/xml');
        const geojson = kml(kmlDoc);
        resolve(geojson);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

const isValidGeometry = (feature) => {
  return feature && feature.geometry && 
    ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'].includes(feature.geometry.type);
};

const convertToPolygon = (feature) => {
  if (!feature || !feature.geometry) {
    throw new Error('Invalid feature: missing geometry');
  }

  if (feature.geometry.type === 'Polygon') {
    return feature.geometry;
  } else if (feature.geometry.type === 'MultiPolygon') {
    return { type: 'Polygon', coordinates: feature.geometry.coordinates[0] };
  } else if (feature.geometry.type.includes('LineString')) {
    const coordinates = feature.geometry.type === 'LineString' ? feature.geometry.coordinates : feature.geometry.coordinates[0];
    const line = turf.lineString(coordinates);
    const buffered = turf.buffer(line, 0.000001, { units: 'degrees' });
    return buffered.geometry;
  }
  throw new Error(`Unsupported geometry type: ${feature.geometry.type}`);
};

const checkOverlap = (feature1, feature2) => {
  try {

    const poly1 = convertToPolygon(feature1);
    const poly2 = convertToPolygon(feature2);

    // Ensure the converted geometries are valid polygons
    if (poly1.type !== 'Polygon' || poly2.type !== 'Polygon') {
      throw new Error('Converted geometries are not polygons');
    }

    // Ensure polygons have valid coordinates
    if (!poly1.coordinates || !poly1.coordinates[0] || poly1.coordinates[0].length < 4) {
      throw new Error('Invalid coordinates for Poly 1');
    }
    if (!poly2.coordinates || !poly2.coordinates[0] || poly2.coordinates[0].length < 4) {
      throw new Error('Invalid coordinates for Poly 2');
    }

    // Check if the polygons are identical
    if (JSON.stringify(poly1) === JSON.stringify(poly2)) {
      return true;
    }

    // Use turf.booleanOverlap instead of turf.intersect
    const overlaps = turf.booleanOverlap(poly1, poly2);
    
    if (overlaps === null) {
      return false;
    }

    return overlaps;
  } catch (error) {
    console.error('Error checking overlap:', error);
    return false;
  }
};

export const checkKMLOverlap = async (files) => {
  try {
    const geojsonFiles = await Promise.all(files.map(parseKMLFile));

    const overlappingFiles = new Set();
    
    for (let i = 0; i < geojsonFiles.length; i++) {
      for (let j = i + 1; j < geojsonFiles.length; j++) {
        const features1 = geojsonFiles[i].features.filter(isValidGeometry);
        const features2 = geojsonFiles[j].features.filter(isValidGeometry);

        for (const feature1 of features1) {
          for (const feature2 of features2) {
            if (checkOverlap(feature1, feature2)) {
              overlappingFiles.add(files[i].name);
              overlappingFiles.add(files[j].name);
              break;
            }
          }
          if (overlappingFiles.has(files[i].name)) break;
        }
        if (overlappingFiles.has(files[i].name)) break;
      }
    }

    return {
      hasOverlap: overlappingFiles.size > 0,
      overlappingFiles: Array.from(overlappingFiles)
    };
  } catch (error) {
    console.error('Error in checkKMLOverlap:', error);
    throw error;
  }
};